import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Select, Tag, Tooltip, Typography } from 'antd';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import moment from 'moment';
import { getAllPaymentLogsAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

// ─── Event colour map ────────────────────────────────────────────────────────
const EVENT_COLORS: Record<string, string> = {
  'order.create.success': 'green',
  'order.create.error': 'red',
  'capture.start': 'blue',
  'capture.success': 'green',
  'capture.error': 'red',
  'capture.signature.fail': 'volcano',
  'capture.transaction.missing': 'orange',
  'verify.result': 'geekblue',
  'recover.start': 'purple',
  'recover.already.processed': 'cyan',
  'recover.success': 'green',
  'recover.error': 'red',
  'recover.razorpay.not.paid': 'gold',
  'recover.no.payment.captured': 'orange',
  'frontend.sdk.open': 'blue',
  'frontend.sdk.success': 'green',
  'frontend.sdk.error': 'red',
  'frontend.capture.call': 'geekblue',
  'frontend.recover.call': 'purple',
};

export const BasicTable: React.FC = () => {
  const [filters, setFilters] = useState({
    event: '',
    razorpay_order_id: '',
    flow: '',
    source: '',
    dateRange: [] as any[],
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const dispatch = useAppDispatch();

  const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params: any = {
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    if (filters.event) params.event = filters.event;
    if (filters.razorpay_order_id) params.razorpay_order_id = filters.razorpay_order_id;
    if (filters.flow) params.flow = filters.flow;
    if (filters.source) params.source = filters.source;
    if (filters.dateRange && filters.dateRange.length === 2) {
      params.dateRange = [
        moment(filters.dateRange[0]).toISOString(),
        moment(filters.dateRange[1]).toISOString(),
      ].join(',');
    }

    dispatch(getAllPaymentLogsAction(params))
      .unwrap()
      .then((response) => {
        setTableData({ data: response.data, loading: false, total: response.total });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch payment logs' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [pagination, filters, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current: 1 })); // reset to page 1 on filter change
  };

  const handleTableChange = (paginationInfo: any) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  const handleClearFilters = () => {
    setFilters({ event: '', razorpay_order_id: '', flow: '', source: '', dateRange: [] });
    setTimeout(() => setPagination({ current: 1, pageSize: 20 }), 100);
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      width: 180,
      render: (val) => moment(val).format('DD MMM YYYY, HH:mm:ss'),
    },
    {
      title: 'Event',
      dataIndex: 'event',
      width: 240,
      render: (val) => (
        <Tag color={EVENT_COLORS[val] || 'default'} style={{ fontFamily: 'monospace' }}>
          {val}
        </Tag>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search event"
            value={selectedKeys[0]}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedKeys(v ? [v] : []);
              handleFilterChange('event', v);
            }}
            onPressEnter={() => confirm()}
            style={{ width: 220, marginBottom: 8, display: 'block' }}
          />
          <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90, marginRight: 8 }}>
            Search
          </Button>
          <Button onClick={() => { clearFilters && clearFilters(); handleFilterChange('event', ''); }} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Razorpay Order ID',
      dataIndex: 'razorpay_order_id',
      width: 220,
      render: (val) => val ? <Text code copyable style={{ fontSize: 11 }}>{val}</Text> : <Text type="secondary">—</Text>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Exact order ID"
            value={selectedKeys[0]}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedKeys(v ? [v] : []);
              handleFilterChange('razorpay_order_id', v);
            }}
            onPressEnter={() => confirm()}
            style={{ width: 220, marginBottom: 8, display: 'block' }}
          />
          <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90, marginRight: 8 }}>
            Search
          </Button>
          <Button onClick={() => { clearFilters && clearFilters(); handleFilterChange('razorpay_order_id', ''); }} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'client_id',
      width: 160,
      render: (val) =>
        val ? (
          <span>
            <div style={{ fontWeight: 600 }}>{val.name || '—'}</div>
            <div style={{ color: '#888', fontSize: 12 }}>{val.phone || ''}</div>
          </span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Flow',
      dataIndex: 'flow',
      width: 100,
      render: (val) => {
        const map: any = { wallet: 'blue', cart: 'purple', unknown: 'default' };
        return <Tag color={map[val] || 'default'}>{val}</Tag>;
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            value={selectedKeys[0] || ''}
            onChange={(v) => { setSelectedKeys(v ? [v] : []); handleFilterChange('flow', v); }}
            style={{ width: 160, marginBottom: 8 }}
            placeholder="All flows"
            allowClear
          >
            <Option value="wallet">Wallet</Option>
            <Option value="cart">Cart</Option>
            <Option value="unknown">Unknown</Option>
          </Select>
          <br />
          <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90, marginRight: 8 }}>
            Filter
          </Button>
          <Button onClick={() => { clearFilters && clearFilters(); handleFilterChange('flow', ''); }} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      width: 110,
      render: (val) => <Tag color={val === 'frontend' ? 'magenta' : 'cyan'}>{val}</Tag>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            value={selectedKeys[0] || ''}
            onChange={(v) => { setSelectedKeys(v ? [v] : []); handleFilterChange('source', v); }}
            style={{ width: 160, marginBottom: 8 }}
            placeholder="All sources"
            allowClear
          >
            <Option value="backend">Backend</Option>
            <Option value="frontend">Frontend</Option>
          </Select>
          <br />
          <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90, marginRight: 8 }}>
            Filter
          </Button>
          <Button onClick={() => { clearFilters && clearFilters(); handleFilterChange('source', ''); }} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Amount (₹)',
      dataIndex: 'amount',
      width: 110,
      render: (val) => val != null ? <span>₹{val}</span> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Meta',
      dataIndex: 'meta',
      width: 260,
      render: (val) =>
        val ? (
          <Tooltip title={<pre style={{ fontSize: 11, maxWidth: 400 }}>{JSON.stringify(val, null, 2)}</pre>}>
            <Text
              style={{ fontFamily: 'monospace', fontSize: 11, cursor: 'pointer', color: '#1677ff' }}
              ellipsis
            >
              {JSON.stringify(val)}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <>
      {/* Date range filter row */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <RangePicker
          showTime
          onChange={(dates) => handleFilterChange('dateRange', dates || [])}
          value={filters.dateRange.length === 2 ? filters.dateRange as any : null}
          placeholder={['From', 'To']}
        />
        <Button onClick={handleClearFilters}>Clear All Filters</Button>
      </div>

      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        rowKey="_id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tableData.total,
          showTotal: (total) => `${total} events`,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 1300 }}
        bordered
        size="small"
      />
    </>
  );
};
